import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { usePage } from "@danishagro/shared/src/contexts/page.context";
import { usePageLocationContext } from "@contexts/PageLocationContext";
import { DA_Hero } from "@danishagro/shared/src/components/organisms/Hero/Hero.component";
import ComponentErrorBoundary from "@danishagro/shared/src/components/molecules/ErrorBoundary/ComponentErrorBoundary.component";
import { DA_NewPageData } from "@interfaces/newPageData.interface";
import { getAbsoluteUrl } from "@danishagro/shared/src/components/organisms/SiteHeader/LanguageSelector/hooks/getAbsolutePath.hook";
import { HeroObj } from "@danishagro/shared/src/interfaces/HeroObj.interface";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DynamicBlocksRenderer } from "@helpers/DynamicBlocksRenderer.helper";
import { BlockData } from "@interfaces/blockData.interface";
import { SSH_HeroList } from "@danishagro/sharedShop/src/components/organisms/HeroList/HeroList.component";
import BlockRenderer from "@helpers/BlockRenderer";
import { useSchemaMarkup } from "@danishagro/shared/src/hooks/useSchemaMarkup.hook";

const DA_HomePage = () => {
    const { setPageIsLoading } = usePage();
    const { pageData: contextPageData, isLoading: contextIsLoading } = usePageLocationContext();
    const [pageData, setPageData] = useState<DA_NewPageData>();
    const [heroData, setHeroData] = useState<HeroObj>();
    const [heroBlocks, setHeroBlocks] = useState<BlockData[]>();
    const [heroImage, setHeroImage] = useState(null);
    const [heroVideo, setHeroVideo] = useState(null);
    const [heroButton, setHeroButton] = useState(null);
    const [heroPosterStart, setHeroPosterStart] = useState(null);
    const [isDynamicHero, setIsDynamicHero] = useState<boolean>(false);

    const { heroObj, customerNumber } = useAppData();

    // Use the custom hook for schema generation
    const dynamicSchemas = useSchemaMarkup(pageData);

    const getImageObject = (data: HeroObj) => {
        const image = data?.props?.image;

        if (!image || !image.props?.umbracoFile) {
            // Return null if the image or umbracoFile is missing
            return null;
        }

        if (image && image?.props?.umbracoFile?.includes("assetID")) {
            const imageWidth: number = 1400;
            const imageHeight: number = 600;
            const imageQuality: number = 80;

            const digizuiteSrc = image?.props?.umbracoFile
                .replace("{QUALITY}", `${imageQuality}`)
                .replace("{WIDTH}", `${imageWidth}`)
                .replace("{HEIGHT}", `${imageHeight}`);

            return {
                url: digizuiteSrc,
                width: imageWidth,
                height: imageHeight,
                alt: image.name,
            };
        } else if (image && image?.props?.umbracoFile) {
            return {
                url: getAbsoluteUrl(image?.props?.umbracoFile),
                width: image.props?.umbracoWidth,
                height: image.props?.umbracoHeight,
                alt: image.name,
            };
        } else {
            return null; // or handle the case where no image is available
        }
    };

    const getVideoObject = (data: HeroObj) => {
        const video = data?.props?.video;

        if (video && video.length > 0 && video[0]?.content?.props?.videoFile) {
            const videoContent = video[0].content;
            const videoProps = videoContent.props;
            const videoUrl = videoProps.videoFile.url;

            let posterStart = null;
            if (videoProps.posterStart) {
                const poster = videoProps.posterStart;
                posterStart = {
                    url: poster.url,
                    width: poster.props?.umbracoWidth
                        ? parseInt(poster.props.umbracoWidth)
                        : undefined,
                    height: poster.props?.umbracoHeight
                        ? parseInt(poster.props.umbracoHeight)
                        : undefined,
                    alt: poster.name,
                };
            }

            return {
                video: {
                    alias: videoContent.alias,
                    url: videoUrl,
                    autoplay: true,
                    controls: false,
                    muted: true,
                    loop: false,
                },
                posterStart: posterStart,
            };
        } else {
            return null;
        }
    };

    // Sync loading state with context
    useEffect(() => {
        setPageIsLoading(contextIsLoading);
    }, [contextIsLoading, setPageIsLoading]);

    // Process page data when it becomes available
    useEffect(() => {
        if (!contextPageData) return;

        // Check if hero array exists
        const heroArray = contextPageData?.props?.hero;
        if (!Array.isArray(heroArray) || heroArray.length === 0) {
            setIsDynamicHero(false);
            setPageData(contextPageData);
            return;
        }

        // Determine hero type
        const firstHero = heroArray[0]?.content || {};
        const firstHeroType = firstHero.alias || "";
        const isDynamic = firstHeroType && firstHeroType !== "segmentHero";

        // map hero blocks and render them dynamic
        if (isDynamic) {
            const heroList = firstHeroType === "heroItem" ? firstHero.props?.heroes : [];
            setHeroBlocks(heroList.map((item) => item.content));
        } else {
            // set hero data from global hero object and render classic hero
            const heroData = heroObj?.[0];

            // Only process hero if heroData exists
            if (heroData) {
                setHeroData(heroData);
                setHeroImage(getImageObject(heroData));

                // Take all buttons, not just the first one - but for classic hero we still use only the first
                const allButtons = Array.isArray(heroData?.props?.buttons)
                    ? heroData.props.buttons
                    : [];
                setHeroButton(allButtons?.length > 0 ? allButtons[0]?.content?.props : null);

                const videoData = getVideoObject(heroData);

                if (videoData) {
                    setHeroVideo(videoData.video);
                    setHeroPosterStart(videoData.posterStart);
                } else {
                    setHeroVideo(null);
                    setHeroPosterStart(null);
                }
            }
        }

        setIsDynamicHero(isDynamic);
        setPageData(contextPageData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextPageData, customerNumber, heroObj]);

    return (
        <>
            <Helmet>
                <title>{pageData?.name}</title>
                <meta name="description" content={pageData?.metaDescription} />
                {/* Dynamic Schema Markup from CMS */}
                {dynamicSchemas.map((schema, index) => (
                    <script key={index} type="application/ld+json">
                        {JSON.stringify(schema)}
                    </script>
                ))}
            </Helmet>

            <div className="frontpage">
                <div className="frontpage-content">
                    {/* "Classic" hero */}
                    {!isDynamicHero && heroData && (
                        <div className="frontpage-hero">
                            <ComponentErrorBoundary showNothing componentName="Hero">
                                <DA_Hero
                                    headline={heroData.props?.headline || ""}
                                    text={heroData.props?.text || ""}
                                    image={
                                        heroImage
                                            ? {
                                                  src: heroImage.url,
                                                  width: heroImage.width,
                                                  height: heroImage.height,
                                                  alt: heroImage.alt,
                                              }
                                            : undefined
                                    }
                                    video={heroVideo}
                                    posterStart={heroPosterStart}
                                    alias={heroData.alias}
                                    link={{
                                        title: heroButton?.label || heroButton?.link?.name || "",
                                        href: heroButton?.link?.url || "",
                                        target: heroButton?.link?.target || "_self",
                                        conventionalRouting: true,
                                    }}
                                    fullWidthMobile
                                    enableTracking={true}
                                />
                            </ComponentErrorBoundary>
                        </div>
                    )}

                    {isDynamicHero && heroBlocks && (
                        <SSH_HeroList blocks={heroBlocks} BlockRenderer={BlockRenderer} />
                    )}

                    {pageData?.props?.modules && (
                        <DynamicBlocksRenderer
                            blocks={pageData.props.modules.map((mod: any) => ({
                                ...mod.content,
                                version: mod.version,
                            }))}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default DA_HomePage;
